interface HeroProps {
  onGoContribute: () => void;
  onGoFeed: () => void;
}

export default function Hero({ onGoContribute, onGoFeed }: HeroProps) {
  return (
    <section id="top" className="relative overflow-hidden bg-background-50">
      {/* decorative blobs */}
      <div className="pointer-events-none absolute -left-24 top-10 h-64 w-64 rounded-full bg-accent-200/50 blur-3xl"></div>
      <div className="pointer-events-none absolute right-0 top-40 h-72 w-72 rounded-full bg-primary-200/50 blur-3xl"></div>

      <div className="relative mx-auto grid max-w-[1240px] items-center gap-10 px-4 pb-10 pt-28 md:px-6 md:pt-36 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
        {/* copy */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-800">
              <i className="ri-fire-line"></i> 今日爆梗 9 连更
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-semibold text-foreground-600">
              <i className="ri-megaphone-line"></i> 笑中带泪 · 每日上新
            </span>
          </div>

          <h1 className="mt-5 font-heading text-[2.6rem] leading-[1.1] text-foreground-950 md:text-6xl">
            这里没有冷场
            <br />
            只有<span className="relative inline-block text-primary-600">
              热梗
              <svg viewBox="0 0 120 14" className="absolute -bottom-2 left-0 w-full text-accent-500" aria-hidden="true">
                <path d="M3 11 C 30 4, 60 4, 117 8" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
              </svg>
            </span>
            ，
            <br />
            笑到肚子疼的那种
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            55吃瓜爆笑吐槽基地，收集全网最离谱的<span className="font-semibold text-foreground-800">沙雕剧情</span>与<span className="font-semibold text-foreground-800">社死现场</span>，配上比正片还精彩的
            吐槽弹幕和表情包收割机。刷到这里，你的嘴角很难不开始抽动。
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={onGoFeed}
              className="group flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              开吃今日的瓜
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
            </button>
            <button
              onClick={onGoContribute}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-3.5 text-base font-semibold text-background-50 transition-colors hover:bg-foreground-800"
            >
              <i className="ri-chat-smile-line"></i>
              贡献一段社死经历
            </button>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-emotion-laugh-fill text-accent-600"></i> 8600+ 条沙雕爆梗
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-emotion-happy-line text-primary-600"></i> 12w+ 表情包下载
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-24-hours-line text-secondary-700"></i> 每天都要笑到累
            </span>
          </div>
        </div>

        {/* visual panel */}
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="absolute -right-2 -top-3 z-10 rotate-6 animate-wiggle rounded-2xl bg-accent-500 px-4 py-2 font-heading text-background-50 shadow-none md:-right-4">
            今日份快乐请查收
          </div>
          <div className="absolute -left-3 top-10 z-10 rounded-2xl bg-background-50 p-2 shadow-[0_0_0_2px_oklch(var(--background-200))] animate-bob">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary-100 text-primary-600 md:h-11 md:w-11">
              <i className="ri-lightbulb-flash-line text-xl"></i>
            </span>
          </div>

          <div className="relative overflow-hidden rounded-[2rem] bg-foreground-950">
            <img
              src="https://readdy.ai/api/search-image?query=flat%20cartoon%20illustration%20of%20a%20crowd%20of%20happy%20people%20laughing%20with%20tears%20while%20popcorn%20and%20watermelon%20slices%20float%20around%2C%20party%20confetti%20and%20bright%20comic%20speech%20bubbles%2C%20warm%20cream%20background%2C%20vivid%20coral%20lime%20and%20mustard%20accents%2C%20editorial%20style%2C%20no%20text&width=900&height=720&seq=hero-2026-main&orientation=squarish"
              alt="一群网友在瓜田里边吃瓜边笑得前仰后合的卡通插画"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
            <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-background-50/95 px-4 py-3 backdrop-blur">
              <p className="font-heading text-base text-foreground-950 md:text-lg">每日签到：今天你为谁社死过？</p>
              <p className="mt-0.5 text-xs text-foreground-500">评论区常驻弹幕，笑点已就位 · 老规矩：先笑，再心疼。</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}